import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  createStyles,
  FormControlLabel,
  Grid,
  makeStyles,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";

import _ from "lodash";
import WithLoading from "../../../../utils/WithLoading";
import { CustomTheme } from "../../../../types/customTheme";
import { handleDownloadPdf } from "./convert/handlePdf";
import { handleDownloadExcel } from "./convert/handleExcel";
import LocationDetailsCard from "../../../../components/common/LocationDetailsCard";
import MenuButtonCommon from "../../../../components/buttons/MenuButtonCommon";
import GetAppIcon from "@material-ui/icons/GetApp";
import { Autocomplete, createFilterOptions } from "@material-ui/lab";
import DropDown from "./DropDown";
import SwitchCommon from "../../../../components/switches/SwitchCommon";
import NormalSalesTable from "./NormalSalesTable";
import WeeklySalesTable from "./WeeklySalesTable";
import { handleDownloadPdfWeekly } from "./convert/handlePdfWeekly";
import { handleDownloadExcelWeekly } from "./convert/handleExcelWeekly";
import SelectedDaysDropdown from "./SelectedDaysDropdown";
export interface ShiftHeaderProps {
  isLoading: any;
  startDate: any;
  endDate: any;
  filterDetails: any;
  locationSelectedList: any;
  handleOnClickText: any;
  driverSummaryNodeList: any;
  weeklySales: any;
  weeklySalesCountValue: any;
  weeklySalesTotalValue: any;
}

const dropDownDays = [
  { id: "monday", label: "Monday" },
  { id: "tuesday", label: "Tuesday" },
  { id: "wednesday", label: "Wednesday" },
  { id: "thursday", label: "Thursday" },
  { id: "friday", label: "Friday" },
  { id: "saturday", label: "Saturday" },
  { id: "sunday", label: "Sunday" },
];

const dropDownDaysList = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const useStyles = makeStyles((theme: any) =>
  createStyles({
    tableContainer: {
      overflowX: "auto", // Enable horizontal scroll if needed
      backgroundColor: "transparent",
    },
    table: {
      borderCollapse: "separate",
      borderSpacing: "4px", // Adjust spacing here (horizontal vertical)
    },

    dateCell: {
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      overflow: "hidden",
      padding: "8px",
      borderBottom: "none",
    },
    hourCell: {
      position: "sticky",
      width: "150px",
      left: 0,
      backgroundColor: theme.palette.background.default, // Ensure background is solid for fixed column
      zIndex: 1, // Ensure it stays on top of other cells when scrolling
      fontWeight: "bold",
      borderBottom: "none",
    },
    titleColor: {
      color: theme.palette.custom.orange.contrastText,
    },
    hourHeaderCell: {
      position: "sticky",
      width: "150px",
      left: 0,
      backgroundColor: theme.palette.background.default,
      zIndex: 3, // Ensure the header stays on top
      fontWeight: "bold",
      borderBottom: "none",
    },
    autoComplete: {
      marginTop: "4px",
      "& .MuiAutocomplete-input": {
        color: theme.palette.custom.orange.contrastText,
      },
      "& .MuiInputBase-root": {
        color: "inherit",
        backgroundColor: theme.palette.background.entity_highlight_background,
        borderRadius: 10,
      },
      [`& fieldset`]: {
        borderRadius: 10,
        border: "none",
        color: theme.palette.custom.orange.contrastText,
      },
    },
  }),
);

const convertTo12HourFormat = (hour: any) => {
  let period = hour >= 12 ? "PM" : "AM";
  let adjustedHour = hour % 12;
  adjustedHour = adjustedHour === 0 ? 12 : adjustedHour; // Handle midnight and noon

  // Ensure the hour is always two digits
  let formattedHour = adjustedHour < 10 ? `0${adjustedHour}` : adjustedHour;

  return `${formattedHour}:00${period}`;
};

const getDateRange = (
  startDate: string,
  endDate: string,
  selectedDaysList: any,
): string[] => {
  const result: string[] = [];
  let current = new Date(startDate + "T00:00:00Z"); // UTC mode to avoid timezone bugs
  const end = new Date(endDate + "T00:00:00Z");

  while (current <= end) {
    const date = new Date(current);
    const dayOfWeek = date.toLocaleDateString("en-US", { weekday: "long" }); // e.g., "Thu"
    if (selectedDaysList.includes(dayOfWeek.toLowerCase())) {
      result.push(current.toISOString().split("T")[0]);
    }

    // Add one day in UTC to avoid DST issues
    current = new Date(current.getTime() + 24 * 60 * 60 * 1000);
  }

  return result;
};

/**
 * HourlySalesReportNode Component:
 *
 * This component handles the header section of the Shift component, including
 * sorting controls, location selection, and shift generation. It displays shift
 * information in a table-like format with sortable columns and provides options
 * for selecting shifts, generating reports, and handling user interactions.
 */
const HourlySalesReportNode: React.FunctionComponent<ShiftHeaderProps> = ({
  isLoading,
  startDate,
  endDate,
  filterDetails,
  locationSelectedList,
  handleOnClickText,
  driverSummaryNodeList,
}) => {
  const classes = useStyles();
  const [isShowWeaklyAverage, setIsShowWeaklyAverage] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedRoleObj, setSelectedRoleObj] = useState<any>({
    id: "all",
    label: "Sales Count & Total",
  });
  const [open, setOpen] = useState(false);
  const [selectedDays, setSelectedDays] = useState<any>(dropDownDays);
  const [selectedDaysList, setSelectedDaysList] =
    useState<any>(dropDownDaysList);

  const days = getDateRange(startDate, endDate, selectedDaysList);

  function convertUtcDateToUkHour(utcDateString: any) {
    const date = new Date(utcDateString);

    const ukHour = new Intl.DateTimeFormat("en-GB", {
      hour: "numeric",
      hour12: false,
      timeZone: "Europe/London",
    }).format(date);

    return parseInt(ukHour, 10);
  }

  function buildUtcIsoString(dateStr: any, hour: any, minute: any) {
    const pad = (num: any) => String(num).padStart(2, "0");
    return `${dateStr}T${pad(hour)}:${pad(minute)}:00Z`;
  }

  const openExport = Boolean(anchorEl);
  const generateHourlyData = (
    days: string[],
    salesData: {
      [date: string]: { hour: number; saleCount: number; saleTotal: number }[];
    },
  ) => {
    const hours = Array.from({ length: 24 }, (_, i) => i);

    let uniqueValue: number[] = [];
    let valueTotalUnique: number[] = [];

    // Preprocess salesData to shift 23 UTC => 0 UK sales to next day
    const salesDataMap: {
      [date: string]: { [hour: number]: { count: number; value: number } };
    } = {};

    days.forEach((dayLabel) => {
      const sales = salesData[dayLabel] || [];
      sales.forEach((s) => {
        const utcIso = buildUtcIsoString(dayLabel, s.hour, 0);
        const ukHour = convertUtcDateToUkHour(utcIso);

        let targetDay = dayLabel;
        let targetHour = ukHour;

        // If hour is 23 UTC and converts to 0 UK, shift to next day
        if (s.hour === 23 && ukHour === 0) {
          let nextDay = new Date(dayLabel);

          // Add one day in UTC to avoid DST issues
          nextDay = new Date(nextDay.getTime() + 24 * 60 * 60 * 1000);
          targetDay = nextDay.toISOString().split("T")[0];
          targetHour = 0;
        }

        if (!salesDataMap[targetDay]) {
          salesDataMap[targetDay] = {};
        }

        salesDataMap[targetDay][targetHour] = {
          count: s.saleCount,
          value: s.saleTotal,
        };
      });
    });

    // Build the hourlyData structure
    const hourlyData = hours.map((hourLabel) => {
      const rowData = {
        hour: convertTo12HourFormat(hourLabel),
        data: days.map((dayLabel) => {
          const dataForDay = salesDataMap[dayLabel]?.[hourLabel];
          if (dataForDay) {
            uniqueValue.push(dataForDay.count);
            valueTotalUnique.push(dataForDay.value);
          }
          return dataForDay
            ? {
                count: dataForDay.count,
                value: dataForDay.value,
                date: dayLabel,
              }
            : { count: 0, value: 0, date: dayLabel };
        }),
      };
      return rowData;
    });

    uniqueValue.sort((a, b) => a - b);
    const value = [...new Set(uniqueValue)];
    valueTotalUnique.sort((a, b) => a - b);
    const valueTotal = [...new Set(valueTotalUnique)];

    return { value, hourlyData, valueTotal };
  };

  const isDayIncluded = (dayIndex: any) => {
    const daysOfWeek = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];
    const dayName = daysOfWeek[dayIndex];
    return !selectedDaysList.includes(dayName);
  };

  const getWeeklySalesByHourUk = (salesData: any, count: any) => {
    const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    const output = Array.from({ length: 24 }, (_, hour) => ({
      hour: new Date(
        `1970-01-01T${hour.toString().padStart(2, "0")}:00:00`,
      ).toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
      data: Array.from({ length: 7 }, () => ({ count: 0, total: 0 })),
    }));

    const valueUnique: number[] = [];
    const valueTotalUnique: number[] = [];

    const buildUtcIsoString = (
      dateStr: string,
      hour: number,
      minute: number,
    ) => {
      const pad = (num: number) => String(num).padStart(2, "0");
      return `${dateStr}T${pad(hour)}:${pad(minute)}:00Z`;
    };

    const convertUtcToUkHour = (isoStr: string) => {
      const date = new Date(isoStr);
      const ukHour = new Intl.DateTimeFormat("en-GB", {
        hour: "numeric",
        hour12: false,
        timeZone: "Europe/London",
      }).format(date);
      return parseInt(ukHour, 10);
    };

    const convertUtcToUkWeekdayIndex = (isoStr: string) => {
      const date = new Date(isoStr);
      const weekDayStr = new Intl.DateTimeFormat("en-GB", {
        weekday: "short",
        timeZone: "Europe/London",
      }).format(date); // e.g., 'Mon'
      return daysOfWeek.indexOf(weekDayStr);
    };

    // Process sales data
    Object.entries(salesData).forEach(([dateStr, sales]: any, index: any) => {
      if (sales !== 1) {
        sales.forEach((s: any) => {
          const utcIso = buildUtcIsoString(dateStr, s.hour, 0);
          let ukHour = convertUtcToUkHour(utcIso);
          let weekDayIndex = convertUtcToUkWeekdayIndex(utcIso);

          // Handle 23 UTC => 0 UK hour shift to next day
          if (s.hour === 23 && ukHour === 0) {
            const adjustedDate = new Date(
              new Date(dateStr).getTime() + 86400000,
            );
            const adjustedIso = buildUtcIsoString(
              adjustedDate.toISOString().split("T")[0],
              0,
              0,
            );
            ukHour = 0;
            weekDayIndex = convertUtcToUkWeekdayIndex(adjustedIso);
          }

          if (weekDayIndex === -1) return;
          if (isDayIncluded(weekDayIndex)) return;

          const entry = output[ukHour].data[weekDayIndex];

          // entry.count=  Math.round(entry.count / count[weekDayIndex])
          entry.count += s.saleCount / count[weekDayIndex];
          entry.total += s.saleTotal / count[weekDayIndex];
          // entry.count = parseFloat(entry.count.toFixed(0));

          valueUnique.push(entry.count);
          valueTotalUnique.push(parseFloat(entry.total.toFixed(2)));
        });
      }
    });
    valueUnique.sort((a, b) => a - b);
    valueTotalUnique.sort((a, b) => a - b);

    const valueTotal = [...new Set(valueTotalUnique)];
    const value = [...new Set(valueUnique)];

    return { output, value, valueTotal };
  };

  const [weeklySales, setWeeklySales] = useState<any>([]);
  const [weeklySalesCountValue, setWeeklySalesCountValue] = useState<any>([]);
  const [weeklySalesTotalValue, setWeeklySalesTotalValue] = useState<any>([]);
  const [value, setValue] = useState<any>([]);
  const [dummyData, setDummyData] = useState<any>([]);
  const [valueTotal, setValueTotal] = useState<any>([]);

  const countWeekdays = (startDateStr: any, endDateStr: any) => {
    const startDate = new Date(startDateStr);
    const endDate = new Date(endDateStr);

    const weekdays = [0, 1, 2, 3, 4, 5, 6];
    const count: any = {
      0: 0,
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0,
      6: 0,
    };

    const date = new Date(startDate);
    while (date <= endDate) {
      const day = weekdays[date.getDay()];
      count[day]++;
      date.setDate(date.getDate() + 1);
    }

    // Remove days with 0 counts (optional)
    Object.keys(count).forEach((key) => {
      if (count[key] === 0) delete count[key];
    });

    return count;
  };

  useEffect(() => {
    const count = countWeekdays(startDate, endDate);
    const {
      output,
      value: weeklySalesCountValue,
      valueTotal: weeklySalesTotalValue,
    } = getWeeklySalesByHourUk(driverSummaryNodeList, count);

    setWeeklySales(output);
    setWeeklySalesCountValue(weeklySalesCountValue);
    setWeeklySalesTotalValue(weeklySalesTotalValue);

    const {
      value,
      hourlyData: dummyData,
      valueTotal,
    } = generateHourlyData(days, driverSummaryNodeList);

    setValue(value);
    setDummyData(dummyData);
    setValueTotal(valueTotal);
  }, [driverSummaryNodeList, selectedDaysList, selectedRoleObj]);
  const theme: CustomTheme = useTheme();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChangeRole = (e: any, role: any) => {
    // Update the state with the selected role
    setSelectedRoleObj(role);
  };

  const handleChangeSelectedDays = (
    event: any,
    selectedOptions: any,
    reason: any,
  ) => {
    // Update the state with the selected role
    setSelectedDaysList(selectedOptions.map((option: any) => option.id));
    setSelectedDays(selectedOptions);
  };
  const handleShowWeeklyAverage = () => {
    setIsShowWeaklyAverage(!isShowWeaklyAverage);
  };
  const daysWeekly = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const maxWidthLocationChips = useMediaQuery("(max-width: 600px)");

  return (
    <>
      <Grid
        container
        style={{
          marginBottom: "20px",
          marginTop: "8px",
        }}
      >
        <Grid item xs={12}>
          <div
            style={
              maxWidthLocationChips
                ? { display: "block" }
                : {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }
            }
          >
            <LocationDetailsCard
              locationSelectedList={locationSelectedList}
              handleOnClickText={handleOnClickText}
            />
            <div style={{ display: "flex", alignItems: "center" }}>
              <Tooltip title="View average hourly sales by day of the week (Monday to Sunday), based on all selected dates.">
                <FormControlLabel
                  style={{ marginRight: "8px" }}
                  control={<SwitchCommon />}
                  checked={isShowWeaklyAverage}
                  onChange={handleShowWeeklyAverage}
                  label="Show Weekly Averages"
                  labelPlacement="start"
                />
              </Tooltip>
              <SelectedDaysDropdown
                selectedDays={selectedDays}
                handleChangeSelectedDays={handleChangeSelectedDays}
                dropDownDays={dropDownDays}
              />
              <DropDown
                selectedRoleObj={selectedRoleObj}
                handleChangeRole={handleChangeRole}
              />
              {true && (
                <div
                  style={
                    maxWidthLocationChips
                      ? {
                          display: "flex",
                          justifyContent: "end",
                          marginTop: "8px",
                        }
                      : { marginLeft: "16px" }
                  }
                >
                  <MenuButtonCommon
                    id="basic-button"
                    variant="contained"
                    style={{
                      fontSize: 11,
                      height: "38px",
                      paddingLeft: "16px",
                      paddingRight: "16px",
                    }}
                    color="blue"
                    aria-controls={open ? "basic-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? "true" : undefined}
                    onClick={handleClick}
                  >
                    <GetAppIcon />
                    <Typography
                      variant="body1"
                      style={{ paddingLeft: "16px", color: "white" }}
                    >
                      Export
                    </Typography>
                  </MenuButtonCommon>
                  <Menu
                    id="basic-menu"
                    anchorEl={anchorEl}
                    open={openExport}
                    onClose={handleClose}
                    MenuListProps={{
                      "aria-labelledby": "basic-button",
                    }}
                    PaperProps={{
                      elevation: 0,
                      style: {
                        borderRadius: "10px",
                        border: `1px solid ${theme.palette.background.entity_border}`,
                      },
                    }}
                    style={{ top: "56px" }}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                  >
                    {isShowWeaklyAverage ? (
                      <MenuItem
                        onClick={() => {
                          handleDownloadPdfWeekly(
                            daysWeekly,
                            weeklySales,
                            filterDetails,
                            weeklySalesCountValue,
                            selectedRoleObj.id,
                            weeklySalesTotalValue,
                            selectedDaysList,
                          );
                          handleClose();
                        }}
                      >
                        Export as PDF
                      </MenuItem>
                    ) : (
                      <MenuItem
                        onClick={() => {
                          handleDownloadPdf(
                            days,
                            dummyData,
                            filterDetails,
                            value,
                            selectedRoleObj.id,
                            valueTotal,
                          );
                          handleClose();
                        }}
                      >
                        Export as PDF
                      </MenuItem>
                    )}
                    {isShowWeaklyAverage ? (
                      <MenuItem
                        onClick={() => {
                          handleDownloadExcelWeekly(
                            daysWeekly,
                            weeklySales,
                            filterDetails,
                            weeklySalesCountValue,
                            selectedRoleObj.id,
                            weeklySalesTotalValue,
                            selectedDaysList,
                          );
                          handleClose();
                        }}
                      >
                        Export as CSV
                      </MenuItem>
                    ) : (
                      <MenuItem
                        onClick={() => {
                          handleDownloadExcel(
                            days,
                            dummyData,
                            filterDetails,
                            value,
                            selectedRoleObj.id,
                            valueTotal,
                          );
                          handleClose();
                        }}
                      >
                        Export as CSV
                      </MenuItem>
                    )}
                  </Menu>
                </div>
              )}
            </div>
          </div>
        </Grid>
      </Grid>
      <TableContainer className={classes.tableContainer}>
        {isShowWeaklyAverage ? (
          <WeeklySalesTable
            selectedRoleObj={selectedRoleObj}
            days={days}
            startDate={startDate}
            dummyData={weeklySales}
            valueTotal={weeklySalesTotalValue}
            value={weeklySalesCountValue}
            selectedDaysList={selectedDaysList}
          />
        ) : (
          <NormalSalesTable
            selectedRoleObj={selectedRoleObj}
            days={days}
            startDate={startDate}
            dummyData={dummyData}
            valueTotal={valueTotal}
            value={value}
            selectedDaysList={selectedDaysList}
          />
        )}
      </TableContainer>
    </>
  );
};

export default WithLoading(HourlySalesReportNode);
